import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";

type MsgType = "SMS" | "LMS" | "MMS";
type SendStatus = "success" | "fail" | "pending" | "scheduled";

interface SendRecord {
  id: string;
  type: MsgType;
  subject?: string;
  message: string;
  hasImage?: boolean;
  recipientCount: number;
  successCount: number;
  failCount: number;
  senderNumber: string;
  status: SendStatus;
  sentAt: string;
  cost: number;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

const TYPE_STYLE: Record<MsgType, { bg: string; text: string }> = {
  SMS: { bg: "bg-violet-500/20", text: "text-violet-300" },
  LMS: { bg: "bg-pink-500/20", text: "text-pink-300" },
  MMS: { bg: "bg-amber-500/20", text: "text-amber-300" },
};

const STATUS_STYLE: Record<SendStatus, { bg: string; border: string; text: string; icon: string; label: string }> = {
  success: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400", icon: "ri-checkbox-circle-line", label: "성공" },
  fail: { bg: "bg-red-500/10", border: "border-red-500/20", text: "text-red-400", icon: "ri-close-circle-line", label: "실패" },
  pending: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400", icon: "ri-loader-4-line", label: "발송 중" },
  scheduled: { bg: "bg-violet-500/10", border: "border-violet-500/20", text: "text-violet-400", icon: "ri-calendar-schedule-line", label: "예약" },
};

interface Props {
  userId: string;
  userName: string;
  totalSent: number;
}

export default function UserSendHistoryPanel({ userId, userName }: Props) {
  const { session } = useAuth();
  const [history, setHistory] = useState<SendRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<"ALL" | MsgType>("ALL");
  const [statusFilter, setStatusFilter] = useState<"ALL" | SendStatus>("ALL");
  const [page, setPage] = useState(1);
  const PER_PAGE = 5;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const accessToken = session?.access_token;
      if (!accessToken) {
        setError("로그인이 필요합니다.");
        setLoading(false);
        return;
      }

      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/get-admin-send-history?user_id=${encodeURIComponent(userId)}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            apikey: SUPABASE_ANON_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || `HTTP ${res.status}`);
      }

      const json = await res.json();
      const raw = (json.send_history ?? []) as any[];

      // Map raw DB rows to SendRecord shape
      const mapped: SendRecord[] = raw.map((h) => {
        const rawType = (h.type || "SMS").toUpperCase() as MsgType;
        const rawStatus = (h.status || "success") as SendStatus;
        const successCount = h.success_count ?? 0;
        const failCount = h.fail_count ?? 0;
        const recipientCount = h.recipient_count ?? (successCount + failCount);
        return {
          id: String(h.id),
          type: ["SMS", "LMS", "MMS"].includes(rawType) ? rawType : "SMS",
          subject: h.subject || undefined,
          message: h.message || "",
          hasImage: !!h.has_image || rawType === "MMS",
          recipientCount,
          successCount,
          failCount,
          senderNumber: h.sender_number || h.sender_id || "-",
          status: ["success", "fail", "pending", "scheduled"].includes(rawStatus) ? rawStatus : "success",
          sentAt: h.sent_at || h.created_at || "",
          cost: h.total_cost ?? h.cost ?? 0,
        };
      });

      setHistory(mapped);
    } catch (err: any) {
      console.error("[UserSendHistoryPanel] fetch error:", err);
      setError(err.message || "발송 이력을 불러오는 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }, [session, userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-14 text-center">
        <div className="w-8 h-8 border-2 border-rose-500/40 border-t-rose-500 rounded-full animate-spin mb-3"></div>
        <p className="text-gray-500 text-sm">발송 이력 불러오는 중...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-14 text-center">
        <i className="ri-error-warning-line text-3xl text-red-400 mb-2 block"></i>
        <p className="text-red-400 text-sm mb-3">{error}</p>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-white/[0.06] hover:bg-white/[0.1] text-gray-300 rounded-lg text-sm cursor-pointer transition-colors"
        >
          다시 시도
        </button>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-14 text-center">
        <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/[0.04] mb-3">
          <i className="ri-send-plane-line text-2xl text-gray-600"></i>
        </div>
        <p className="text-gray-500 text-sm font-medium">발송 이력 없음</p>
        <p className="text-gray-700 text-xs mt-1">{userName} 회원의 발송 이력이 없습니다</p>
      </div>
    );
  }

  // Aggregate stats
  const totalRecipients = history.reduce((s, h) => s + h.recipientCount, 0);
  const totalSuccess = history.reduce((s, h) => s + h.successCount, 0);
  const totalFail = history.reduce((s, h) => s + h.failCount, 0);
  const totalCost = history.reduce((s, h) => s + h.cost, 0);
  const successRate = totalRecipients > 0 ? Math.round((totalSuccess / totalRecipients) * 100) : 0;
  const smsCount = history.filter((h) => h.type === "SMS").length;
  const lmsCount = history.filter((h) => h.type === "LMS").length;
  const mmsCount = history.filter((h) => h.type === "MMS").length;

  // Filtered list
  const filtered = history.filter((h) => {
    const mt = typeFilter === "ALL" || h.type === typeFilter;
    const ms = statusFilter === "ALL" || h.status === statusFilter;
    return mt && ms;
  });
  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div className="space-y-4">
      {/* Top stats */}
      <div className="grid grid-cols-2 gap-2">
        {/* Success rate — big card */}
        <div className="col-span-2 bg-white/[0.03] border border-white/[0.07] rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest">전체 발송 성공률</p>
            <span className={`text-sm font-bold ${successRate >= 95 ? "text-emerald-400" : successRate >= 80 ? "text-amber-400" : "text-red-400"}`}>
              {successRate}%
            </span>
          </div>
          {/* Progress bar */}
          <div className="h-2 bg-white/[0.06] rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                successRate >= 95 ? "bg-emerald-500" : successRate >= 80 ? "bg-amber-500" : "bg-red-500"
              }`}
              style={{ width: `${successRate}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 text-emerald-400 text-xs">
                <i className="ri-checkbox-circle-line"></i>
                성공 {totalSuccess.toLocaleString()}건
              </span>
              <span className="flex items-center gap-1 text-red-400 text-xs">
                <i className="ri-close-circle-line"></i>
                실패 {totalFail.toLocaleString()}건
              </span>
            </div>
            <span className="text-gray-600 text-xs">총 {totalRecipients.toLocaleString()}건</span>
          </div>
        </div>

        {/* Stats mini */}
        {[
          { label: "발송 횟수", value: `${history.length}회`, icon: "ri-send-plane-line", color: "text-violet-400" },
          { label: "사용 크레딧", value: `₩${totalCost.toLocaleString()}`, icon: "ri-coin-line", color: "text-amber-400" },
        ].map((s) => (
          <div key={s.label} className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.05] shrink-0">
              <i className={`${s.icon} ${s.color} text-sm`}></i>
            </div>
            <div>
              <p className="text-white text-sm font-bold">{s.value}</p>
              <p className="text-gray-500 text-xs">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Message type breakdown */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl px-4 py-3">
        <p className="text-gray-500 text-xs font-semibold uppercase tracking-widest mb-3">메시지 유형 분포</p>
        <div className="flex items-center gap-4">
          {[
            { type: "SMS", count: smsCount, style: TYPE_STYLE.SMS },
            { type: "LMS", count: lmsCount, style: TYPE_STYLE.LMS },
            { type: "MMS", count: mmsCount, style: TYPE_STYLE.MMS },
          ].map(({ type, count, style }) => {
            const pct = history.length > 0 ? Math.round((count / history.length) * 100) : 0;
            return (
              <div key={type} className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${style.bg} ${style.text}`}>{type}</span>
                  <span className="text-gray-400 text-xs">{pct}%</span>
                </div>
                <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${style.bg.replace("/20", "/60")}`} style={{ width: `${pct}%` }} />
                </div>
                <p className="text-gray-600 text-[11px] mt-0.5">{count}건</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Type filter */}
        <div className="flex items-center bg-white/[0.03] border border-white/[0.07] rounded-lg p-0.5 gap-0.5">
          {(["ALL", "SMS", "LMS", "MMS"] as const).map((t) => (
            <button
              key={t}
              onClick={() => { setTypeFilter(t); setPage(1); }}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
                typeFilter === t ? "bg-white/[0.1] text-white" : "text-gray-500 hover:text-gray-300"
              }`}
            >
              {t === "ALL" ? "전체" : t}
            </button>
          ))}
        </div>

        {/* Status filter */}
        <div className="flex items-center bg-white/[0.03] border border-white/[0.07] rounded-lg p-0.5 gap-0.5">
          {(["ALL", "success", "fail", "scheduled"] as const).map((s) => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
                statusFilter === s ? "bg-white/[0.1] text-white" : "text-gray-500 hover:text-gray-300"
              }`}
            >
              {s === "ALL" ? "전체" : STATUS_STYLE[s].label}
            </button>
          ))}
        </div>

        <span className="text-gray-600 text-xs ml-auto">{filtered.length}건</span>
      </div>

      {/* History list */}
      <div className="space-y-2 max-h-[320px] overflow-y-auto pr-0.5">
        {paged.length === 0 ? (
          <div className="py-8 text-center text-gray-600 text-sm">
            <i className="ri-search-line text-xl mb-1.5 block"></i>
            조건에 맞는 이력이 없습니다
          </div>
        ) : (
          paged.map((record) => {
            const typeSt = TYPE_STYLE[record.type];
            const statusSt = STATUS_STYLE[record.status];
            const rateForRecord = record.recipientCount > 0
              ? Math.round((record.successCount / record.recipientCount) * 100)
              : 0;

            return (
              <div
                key={record.id}
                className="bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.1] rounded-xl px-3.5 py-3 transition-colors"
              >
                {/* Top row */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${typeSt.bg} ${typeSt.text}`}>
                      {record.type}
                    </span>
                    {record.subject && (
                      <span className="text-white text-xs font-semibold truncate max-w-[160px]">{record.subject}</span>
                    )}
                    {record.hasImage && (
                      <span className="text-[10px] text-gray-500">
                        <i className="ri-image-line mr-0.5"></i>이미지
                      </span>
                    )}
                  </div>
                  <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold whitespace-nowrap shrink-0 ${statusSt.bg} ${statusSt.border} ${statusSt.text}`}>
                    <i className={statusSt.icon}></i>
                    {statusSt.label}
                  </span>
                </div>

                {/* Message preview */}
                <p className="text-gray-500 text-xs leading-relaxed line-clamp-1 mb-2.5">
                  {record.message}
                </p>

                {/* Bottom row */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    {/* Recipients */}
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <i className="ri-group-line text-gray-600"></i>
                      <span className="whitespace-nowrap">{record.recipientCount.toLocaleString()}명</span>
                    </div>
                    {/* Success/fail breakdown */}
                    {record.status === "success" && (
                      <div className="flex items-center gap-1.5">
                        {record.successCount > 0 && (
                          <span className="text-emerald-400 text-[11px] whitespace-nowrap">
                            <i className="ri-checkbox-circle-line mr-0.5"></i>{record.successCount.toLocaleString()}
                          </span>
                        )}
                        {record.failCount > 0 && (
                          <span className="text-red-400 text-[11px] whitespace-nowrap">
                            <i className="ri-close-circle-line mr-0.5"></i>{record.failCount.toLocaleString()}
                          </span>
                        )}
                        <span className={`text-[11px] font-semibold ${rateForRecord >= 95 ? "text-emerald-400" : rateForRecord >= 80 ? "text-amber-400" : "text-red-400"}`}>
                          ({rateForRecord}%)
                        </span>
                      </div>
                    )}
                    {/* Cost */}
                    <span className="text-gray-600 text-[11px] whitespace-nowrap">
                      {record.cost > 0 ? `₩${record.cost.toLocaleString()}` : "—"}
                    </span>
                  </div>

                  {/* Date + sender */}
                  <div className="text-right shrink-0">
                    <p className="text-gray-500 text-[11px] whitespace-nowrap">{record.sentAt}</p>
                    <p className="text-gray-700 text-[10px] whitespace-nowrap">{record.senderNumber}</p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 pt-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-s-line text-sm"></i>
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-semibold cursor-pointer transition-all ${
                p === page
                  ? "bg-rose-500/20 border border-rose-500/30 text-rose-400"
                  : "text-gray-500 hover:text-white hover:bg-white/[0.05]"
              }`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-right-s-line text-sm"></i>
          </button>
        </div>
      )}
    </div>
  );
}